import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import CodeEditor from '../CodeEditor/CodeEditor';
import { validateAnswerOptimistic } from '../../utils/answerValidator';
import { validateInteractiveAnswer, generateCodingTask, generateMCQ, validateGeneratedTask, getGeneratedTasks } from '../../services/api';
import { formatContent, formatInstructions, formatExpectedOutput } from '../../utils/contentFormatter';
import McqScreen from './McqScreen';
import './CodingScreen.css';

const CodingScreen = ({ screen, onAnswer, userResponse, lessonId, hints = [], subjectArea, screenState }) => {
  const { handleRequest } = useApi();
  // Start with userResponse if provided, otherwise empty
  const [code, setCode] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false); // Track if code is correct
  const [generatedTasks, setGeneratedTasks] = useState({});
  const [showCodingTask, setShowCodingTask] = useState(false);
  const [showMCQ, setShowMCQ] = useState(false);
  const [loading, setLoading] = useState({ coding: false, mcq: false });
  const [generatedCode, setGeneratedCode] = useState('');
  const [codeValidationResult, setCodeValidationResult] = useState(null);
  const [consoleOutput, setConsoleOutput] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const messageHandlerRef = React.useRef(null);

  // Determine language from content or default to javascript
  const language = screen.contentForTeacherOrSystem.includes('html') ? 'html' : 
                  screen.contentForTeacherOrSystem.includes('css') ? 'css' : 'javascript';

  // Load validation result from screenState if available
  useEffect(() => {
    if (screenState?.answered && screenState.feedback) {
      setValidationResult({
        isCorrect: screenState.isCorrect,
        feedback: screenState.feedback
      });
      setIsCorrect(screenState.isCorrect || false);
    }
  }, [screenState]);

  useEffect(() => {
    // Reset state when screen changes
    setGeneratedTasks({});
    setShowCodingTask(false);
    setShowMCQ(false);
    setCodeValidationResult(null);
    
    // Load saved code from localStorage or use userResponse prop
    if (lessonId && screen.id) {
      // Prioritize userResponse from parent, then localStorage, then empty string
      const savedCode = localStorage.getItem(`lesson-code-${lessonId}-${screen.id}`);
      const codeToUse = userResponse || savedCode || '';
      if (codeToUse) {
        setCode(codeToUse);
      }
      
      const savedGeneratedCode = localStorage.getItem(`lesson-generated-code-${lessonId}-${screen.id}`);
      if (savedGeneratedCode) {
        setGeneratedCode(savedGeneratedCode);
      }
      
      // Load saved validation result for generated code
      const savedCodeValidationResult = localStorage.getItem(`lesson-generated-code-validation-${lessonId}-${screen.id}`);
      if (savedCodeValidationResult) {
        try {
          setCodeValidationResult(JSON.parse(savedCodeValidationResult));
        } catch (e) {
          console.error('Failed to parse saved validation result:', e);
        }
      }
      
      // Load saved validation result for main code (if not already loaded from screenState)
      if (!screenState?.answered) {
        const savedCodeValidation = localStorage.getItem(`lesson-code-validation-${lessonId}-${screen.id}`);
        if (savedCodeValidation) {
          try {
            const validation = JSON.parse(savedCodeValidation);
            setValidationResult(validation);
            setIsCorrect(validation.isCorrect || false);
          } catch (e) {
            console.error('Failed to parse saved code validation:', e);
          }
        }
      }
    }
    
    // Load existing generated tasks for this screen
    if (lessonId && screen.id) {
      handleRequest(() => getGeneratedTasks(lessonId, screen.id))
        .then(response => {
          const tasks = response.data.tasks || [];
          const tasksMap = {};
          tasks.forEach(t => {
            tasksMap[t.type] = t.task;
          });
          setGeneratedTasks(tasksMap);
          if (tasksMap.coding) setShowCodingTask(true);
          if (tasksMap.mcq) setShowMCQ(true);
        })
        .catch(err => console.error('Kunne ikke laste genererte oppgaver:', err));
    }
  }, [lessonId, screen.id, handleRequest, userResponse]);

  // Update code when userResponse prop changes (from parent component)
  useEffect(() => {
    if (userResponse !== undefined && userResponse !== code && userResponse !== '') {
      setCode(userResponse);
    }
  }, [userResponse]); // Only depend on userResponse, not code to avoid loops

  const handleGenerateCodingTask = async () => {
    setLoading(prev => ({ ...prev, coding: true }));
    try {
      const response = await handleRequest(generateCodingTask, lessonId, { screenId: screen.id });
      setGeneratedTasks(prev => ({ ...prev, coding: response.data.task }));
      setShowCodingTask(true);
    } catch (error) {
      console.error('Kunne ikke generere kodeoppgave:', error);
    } finally {
      setLoading(prev => ({ ...prev, coding: false }));
    }
  };

  const handleGenerateMCQ = async () => {
    setLoading(prev => ({ ...prev, mcq: true }));
    try {
      const response = await handleRequest(generateMCQ, lessonId, { screenId: screen.id });
      setGeneratedTasks(prev => ({ ...prev, mcq: response.data.question }));
      setShowMCQ(true);
    } catch (error) {
      console.error('Kunne ikke generere flervalgsspørsmål:', error);
    } finally {
      setLoading(prev => ({ ...prev, mcq: false }));
    }
  };

  const handleSubmitGeneratedCode = async () => {
    if (!generatedCode.trim()) return;
    setLoading(prev => ({ ...prev, coding: true }));
    try {
      const response = await handleRequest(validateGeneratedTask, lessonId, {
        screenId: screen.id,
        taskType: 'coding',
        answer: generatedCode
      });
      setCodeValidationResult(response.data);
      
      // Save validation result to localStorage
      if (lessonId && screen.id) {
        localStorage.setItem(`lesson-generated-code-validation-${lessonId}-${screen.id}`, JSON.stringify(response.data));
      }
      
      if (onAnswer) {
        onAnswer(generatedCode, response.data);
      }
    } catch (error) {
      console.error('Kunne ikke validere kode:', error);
    } finally {
      setLoading(prev => ({ ...prev, coding: false }));
    }
  };

  const handleMCQAnswer = async (answer, result) => {
    if (onAnswer) {
      onAnswer(answer, result);
    }
  };

  const handleRunCode = () => {
    if (!code.trim()) {
      setConsoleOutput([{ 
        type: 'error', 
        message: 'Skriv litt kode før du kjører', 
        timestamp: new Date().toISOString() 
      }]);
      return;
    }
    
    setIsRunning(true);
    // Clear console output
    setConsoleOutput([]);
    
    // Force a re-render to ensure console panel is visible
    setTimeout(() => {
      setConsoleOutput([{ 
        type: 'info', 
        message: 'Starter kjøring...', 
        timestamp: new Date().toISOString() 
      }]);
    }, 10);
    
    // Use setTimeout to ensure state is updated before execution
    setTimeout(() => {
      try {
        let codeToRun = code;
        let isHTML = language === 'html';
        
        if (isHTML) {
          // Pre-process HTML: wrap all inline script tags
          let html = code;
          html = html.replace(/<script([^>]*)>([\s\S]*?)<\/script>/gi, (match, attributes, scriptContent) => {
            if (attributes && attributes.includes('src')) {
              return match;
            }
            if (attributes && attributes.includes('data-console-capture')) {
              return match;
            }
            return `<script${attributes} data-console-capture="true">(function() {
              try {
                ${scriptContent}
              } catch (error) {
                console.error(error.toString() + '\\n' + error.stack);
              }
            })();</script>`;
          });
          
          const consoleCaptureScript = `<script>
            (function() {
              const outputs = [];
              const addOutput = (type, ...args) => {
                const message = args.map(arg => {
                  if (typeof arg === 'object') {
                    try {
                      return JSON.stringify(arg, null, 2);
                    } catch (e) {
                      return String(arg);
                    }
                  }
                  return String(arg);
                }).join(' ');
                outputs.push({ type, message, timestamp: new Date().toISOString() });
              };
              
              const originalLog = console.log.bind(console);
              const originalError = console.error.bind(console);
              const originalWarn = console.warn.bind(console);
              const originalInfo = console.info.bind(console);
              
              console.log = function(...args) {
                originalLog(...args);
                addOutput('log', ...args);
                sendOutputs();
              };
              
              console.error = function(...args) {
                originalError(...args);
                addOutput('error', ...args);
                sendOutputs();
              };
              
              console.warn = function(...args) {
                originalWarn(...args);
                addOutput('warn', ...args);
                sendOutputs();
              };
              
              console.info = function(...args) {
                originalInfo(...args);
                addOutput('info', ...args);
                sendOutputs();
              };
              
              const sendOutputs = () => {
                if (window.parent && window.parent.postMessage) {
                  window.parent.postMessage({ 
                    type: 'console-output', 
                    screenId: '${screen.id}',
                    outputs: [...outputs] 
                  }, '*');
                }
              };
              
              if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => {
                  setTimeout(() => sendOutputs(), 300);
                });
              } else {
                setTimeout(() => sendOutputs(), 300);
              }
              
              window.addEventListener('load', () => {
                setTimeout(() => sendOutputs(), 300);
              });
              
              setInterval(() => {
                if (outputs.length > 0) {
                  sendOutputs();
                }
              }, 500);
            })();
          </script>`;
          
          let htmlWithConsoleCapture;
          if (html.includes('<head')) {
            htmlWithConsoleCapture = html.replace(/<head[^>]*>/i, (match) => match + consoleCaptureScript);
          } else if (html.includes('<html')) {
            htmlWithConsoleCapture = html.replace(/<html[^>]*>/i, (match) => match + '<head>' + consoleCaptureScript + '</head>');
          } else {
            htmlWithConsoleCapture = `<!DOCTYPE html>
<html>
<head>
${consoleCaptureScript}
</head>
<body>
${html}
</body>
</html>`;
          }
          
          codeToRun = htmlWithConsoleCapture;
        }
        
        if (isHTML) {
          // Remove any existing handler
          if (messageHandlerRef.current) {
            window.removeEventListener('message', messageHandlerRef.current);
          }
          
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.id = `console-iframe-${screen.id}`;
          iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
          
          const messageHandler = (event) => {
            if (event.data && event.data.type === 'console-output' && event.data.screenId === screen.id) {
              setConsoleOutput(prev => {
                const existingOutputs = prev || [];
                const newOutputs = event.data.outputs || [];
                const mergedOutputs = [...existingOutputs];
                newOutputs.forEach(newOutput => {
                  const isDuplicate = mergedOutputs.some(
                    existing => existing.message === newOutput.message && 
                               existing.timestamp === newOutput.timestamp
                  );
                  if (!isDuplicate) {
                    mergedOutputs.push(newOutput);
                  }
                });
                return mergedOutputs;
              });
            }
          };
          
          messageHandlerRef.current = messageHandler;
          window.addEventListener('message', messageHandler);
          
          iframe.onload = () => {
            setTimeout(() => {
              setIsRunning(false);
            }, 1000);
          };
          
          iframe.srcdoc = codeToRun;
          document.body.appendChild(iframe);
          
          setTimeout(() => {
            if (messageHandlerRef.current) {
              window.removeEventListener('message', messageHandlerRef.current);
              messageHandlerRef.current = null;
            }
            const iframeEl = document.getElementById(`console-iframe-${screen.id}`);
            if (iframeEl && document.body.contains(iframeEl)) {
              document.body.removeChild(iframeEl);
            }
            setIsRunning(false);
          }, 10000);
        } else {
          // For JavaScript, execute directly
          const outputs = [];
          const originalLog = console.log.bind(console);
          const originalError = console.error.bind(console);
          const originalWarn = console.warn.bind(console);
          const originalInfo = console.info.bind(console);
          
          const addOutput = (type, ...args) => {
            const message = args.map(arg => {
              if (typeof arg === 'object') {
                try {
                  return JSON.stringify(arg, null, 2);
                } catch (e) {
                  return String(arg);
                }
              }
              return String(arg);
            }).join(' ');
            const outputEntry = {
              type,
              message,
              timestamp: new Date().toISOString()
            };
            outputs.push(outputEntry);
            // Update state immediately for each output
            setConsoleOutput(prev => {
              const newOutputs = [...(prev || []), outputEntry];
              return newOutputs;
            });
          };
          
          console.log = function(...args) {
            originalLog(...args);
            addOutput('log', ...args);
          };
          
          console.error = function(...args) {
            originalError(...args);
            addOutput('error', ...args);
          };
          
          console.warn = function(...args) {
            originalWarn(...args);
            addOutput('warn', ...args);
          };
          
          console.info = function(...args) {
            originalInfo(...args);
            addOutput('info', ...args);
          };
          
          try {
            // Execute the code directly - console overrides are already in place
            // Use Function constructor to ensure proper scope
            const func = new Function(codeToRun);
            func();
            // All console.log calls should be captured by our overridden methods above
          } catch (error) {
            addOutput('error', error.toString() + '\n' + (error.stack || ''));
          } finally {
            // Wait a bit before restoring to catch any delayed console calls
            setTimeout(() => {
              // Restore console methods
              console.log = originalLog;
              console.error = originalError;
              console.warn = originalWarn;
              console.info = originalInfo;
              
              // Final state update
              setConsoleOutput(prev => prev || []);
              setIsRunning(false);
            }, 300);
          }
        }
      } catch (error) {
        setConsoleOutput(prev => [...prev, { 
          type: 'error', 
          message: error.toString(), 
          timestamp: new Date().toISOString() 
        }]);
        setIsRunning(false);
      }
    }, 0);
  };

  // Cleanup message handler on unmount
  useEffect(() => {
    return () => {
      if (messageHandlerRef.current) {
        window.removeEventListener('message', messageHandlerRef.current);
      }
    };
  }, []);

  // Auto-save code to localStorage as user types (debounced)
  useEffect(() => {
    if (lessonId && screen.id && code !== undefined) {
      const timeoutId = setTimeout(() => {
        localStorage.setItem(`lesson-code-${lessonId}-${screen.id}`, code);
      }, 500); // Debounce: save 500ms after user stops typing

      return () => clearTimeout(timeoutId);
    }
  }, [code, lessonId, screen.id]);

  // Auto-save generated code to localStorage as user types (debounced)
  useEffect(() => {
    if (lessonId && screen.id && generatedCode !== undefined) {
      const timeoutId = setTimeout(() => {
        localStorage.setItem(`lesson-generated-code-${lessonId}-${screen.id}`, generatedCode);
      }, 500); // Debounce: save 500ms after user stops typing

      return () => clearTimeout(timeoutId);
    }
  }, [generatedCode, lessonId, screen.id]);

  const handleSubmit = async () => {
    if (!code.trim()) return;

    setValidating(true);
    setValidationResult(null); // Clear previous feedback
    const checkingLogic = screen.checkingLogic || {};

    try {
      // Use AI validation for coding
      const result = await validateAnswerOptimistic(
        code,
        checkingLogic,
        {
          useAI: true,
          lessonId,
          screenId: screen.id,
          apiCall: (id, data) => validateInteractiveAnswer(id, data)
        }
      );

      setValidationResult(result);
      setIsCorrect(result.isCorrect || false);
      setValidating(false);

      // Save validation result to localStorage
      if (lessonId && screen.id) {
        localStorage.setItem(`lesson-code-validation-${lessonId}-${screen.id}`, JSON.stringify(result));
      }

      if (onAnswer) {
        onAnswer(code, result);
      }
    } catch (error) {
      const errorResult = {
        isCorrect: false,
        feedback: error.message || 'Kunne ikke validere koden. Prøv igjen.'
      };
      setValidationResult(errorResult);
      
      // Save error result to localStorage
      if (lessonId && screen.id) {
        localStorage.setItem(`lesson-code-validation-${lessonId}-${screen.id}`, JSON.stringify(errorResult));
      }
      
      setValidating(false);
    }
  };

  return (
    <div className="coding-screen">
      <div className="coding-content">
        <div className="coding-left-panel">
          <div className="coding-instructions">
            <h3>{screen.title}</h3>
            <p className="action-description">{screen.studentActionDescription}</p>
            <div 
              className="instructions-text"
              dangerouslySetInnerHTML={{ __html: formatContent(screen.contentForTeacherOrSystem.replace(/```[\s\S]*?```/g, '')) }}
            />
            {screen.checkingLogic?.expectedOutput && (
              <div className="expected-output">
                <strong>Forventet resultat:</strong>
                <p dangerouslySetInnerHTML={{ __html: formatExpectedOutput(screen.checkingLogic.expectedOutput) }} />
              </div>
            )}
            {/* Add "Gå til eksempel" button if there's example code */}
            {screen.checkingLogic?.exampleCode && (
              <button 
                className="example-button"
                onClick={() => setCode((screen.checkingLogic.exampleCode || '').replace(/\\n/g, '\n'))}
              >
                Gå til eksempel
              </button>
            )}
          </div>

          {validationResult && (
            <div className={`feedback ${validationResult.isCorrect ? 'feedback-correct' : 'feedback-incorrect'}`}>
              <strong>{validationResult.isCorrect ? '✓ Riktig!' : '✗ Ikke riktig'}</strong>
              <p>{validationResult.feedback}</p>
            </div>
          )}

          {hints.length > 0 && (
            <div className="hints-section">
              <h4>Hint:</h4>
              {hints.map((hint, index) => (
                <div key={index} className="hint-item">
                  <strong>Hint {index + 1}:</strong>
                  <p>{hint}</p>
                </div>
              ))}
            </div>
          )}

          <button
            className="submit-code-button"
            onClick={handleSubmit}
            disabled={validating || !code.trim()}
          >
            {validating ? 'Validerer...' : (screen.uiLayoutHints?.primaryButtonLabel || 'Kjør kode')}
          </button>

          {/* Generate Task Buttons */}
          {((subjectArea === 'programming' && !showCodingTask) || (!showMCQ && !generatedTasks.mcq)) && (
            <div className="generate-buttons">
              {subjectArea === 'programming' && !showCodingTask && (
                <button
                  className="generate-button generate-coding"
                  onClick={handleGenerateCodingTask}
                  disabled={loading.coding}
                >
                  {loading.coding ? 'Genererer...' : 'Generer kodeoppgave'}
                </button>
              )}
              {!showMCQ && !generatedTasks.mcq && (
                <button
                  className="generate-button generate-mcq"
                  onClick={handleGenerateMCQ}
                  disabled={loading.mcq}
                >
                  {loading.mcq ? 'Genererer...' : 'Generer flervalgsspørsmål'}
                </button>
              )}
            </div>
          )}

          {/* Generated Coding Task */}
          {subjectArea === 'programming' && showCodingTask && generatedTasks.coding && (
            <div className="generated-task coding-task">
              <div className="generated-task-left">
                <h3>Kodeoppgave</h3>
                <p><strong>Beskrivelse:</strong> {generatedTasks.coding.description}</p>
                <p><strong>Instruksjoner:</strong> <span dangerouslySetInnerHTML={{ __html: formatInstructions(generatedTasks.coding.instructions) }} /></p>
              </div>
              <div className="generated-task-right">
                <CodeEditor
                  value={generatedCode}
                  onChange={setGeneratedCode}
                  language="javascript"
                  height="500px"
                />
                {codeValidationResult && (
                  <div className={`feedback ${codeValidationResult.isCorrect ? 'feedback-correct' : 'feedback-incorrect'}`}>
                    {codeValidationResult.feedback}
                  </div>
                )}
                <button
                  className="submit-code-button"
                  onClick={handleSubmitGeneratedCode}
                  disabled={loading.coding || !generatedCode.trim()}
                >
                  {loading.coding ? 'Validerer...' : 'Kjør kode'}
                </button>
                {generatedTasks.coding.expectedOutput && (
                  <div className="expected-output-generated">
                    <strong>Forventet resultat:</strong>
                    <p dangerouslySetInnerHTML={{ __html: formatExpectedOutput(generatedTasks.coding.expectedOutput) }} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Generated MCQ */}
          {showMCQ && generatedTasks.mcq && (
            <div className="generated-task mcq-task">
              <McqScreen
                screen={generatedTasks.mcq}
                onAnswer={handleMCQAnswer}
              />
            </div>
          )}
        </div>

        <div className="coding-right-panel">
          <div className="coding-editor-area">
            <div className="coding-editor-header">
              <h4>Koden din ({language.toUpperCase()})</h4>
              <button
                className="run-code-button"
                onClick={handleRunCode}
                disabled={isRunning || !code.trim()}
                title="Kjør koden og vis output"
              >
                {isRunning ? 'Kjører...' : 'Kjør kode'}
              </button>
            </div>
            <CodeEditor
              value={code}
              onChange={setCode}
              language={language}
              height="100%"
            />
          </div>
          
          {/* Console Output Panel - Always visible */}
          <div className="console-panel" style={{ display: 'flex' }}>
            <div className="console-header">
              <h4>Konsoll</h4>
              <button
                className="clear-console-button"
                onClick={() => setConsoleOutput([])}
                title="Tøm konsoll"
              >
                Tøm
              </button>
            </div>
            <div className="console-output">
              {consoleOutput && consoleOutput.length > 0 ? (
                consoleOutput.map((output, index) => (
                  <div key={index} className={`console-line console-${output.type || 'log'}`}>
                    <span className="console-type">{output.type || 'log'}:</span>
                    <span className="console-message">{output.message || String(output)}</span>
                  </div>
                ))
              ) : (
                <div className="console-empty">Ingen output ennå. Klikk "Kjør kode" for å kjøre koden din.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodingScreen;
